/* rewind-dialog.js — Undo confirmation dialog
 *
 * Caller: timeline.js / promotion.js / agent-activity.js — openUndoDialog(selector, onDone, conn?)
 *   selector: { entry_ids:[...] } | { upto_entry_id } | { group_id } | { run_id }
 *   onDone:   refresh callback, fired after execute (success or failure)
 *   conn:     optional explicit target connection { cid, label, env_role }. When
 *             provided, undoPreview/undoExecute are scoped to conn.cid and the prod
 *             typed-confirm gate reads conn.env_role/conn.label instead of the active
 *             connection's metadata. When omitted, behavior is unchanged (active conn).
 *
 * Flow: show modal → preview plan → step list with badges →
 *       opt-in checkboxes (default unchecked/safe) →
 *       [hosted/prod only] typed-confirm gate →
 *       Undo / Cancel → result state (reversed/skipped/failed) or graceful error.
 *
 * Hosted mode: uses api.undoPreview (POST /api/connections/{cid}/undo/preview) and
 *   api.undoExecute (POST /api/connections/{cid}/undo) — ownership-gated, JWT-authed.
 *   For prod connections (env_role === "prod"), the user must type the connection label
 *   (or "CONFIRM" when the label is empty) before the Undo button is enabled.
 * Local mode: falls back to api.rewindPreview / api.rewindExecute — unchanged.
 */

import { esc } from "./state.js";
import { api } from "./api.js";
import { createDialog, renderDialog } from "./dialog.js";

// Singleton dialog element — created once, reused
let _dlg;

function _ensure() {
  if (_dlg) return _dlg;
  _dlg = createDialog("rw-dialog confirm-dialog");
  return _dlg;
}

// Reversibility → [badge-class, label]
const REV_INFO = {
  reversible:   ["rev-ok",   "Reversible"],
  risky:        ["rev-risk", "Risky"],
  irreversible: ["rev-no",   "Record-only"],
};

// Reverse action → icon class
const ACTION_ICON = {
  delete:       "ic-trash",
  restore:      "ic-undo",
  recreate:     "ic-plus",
  file_restore: "ic-undo",
};

function _stepRows(steps) {
  if (!steps.length) return '<div class="rw-empty">Nothing to undo.</div>';
  return steps.map((s) => {
    const [cls, lbl] = REV_INFO[s.reversibility] || ["rev-no", s.reversibility || "?"];
    const opic = ACTION_ICON[s.action] || "ic-edit";
    // Escape all journal-derived strings
    const targetStr = esc(
      typeof s.target === "object" && s.target !== null
        ? JSON.stringify(s.target)
        : String(s.target ?? "")
    );
    return `<div class="rw-step">
      <i class="ic ic-sm ${opic}"></i>
      <span class="rw-t">${esc(s.action || "")} <b>${esc(s.family || "")}</b> ${targetStr}</span>
      <span class="rev-badge ${cls}"><span class="rev-dot"></span>${lbl}</span>
    </div>`;
  }).join("");
}

// _renderPreview — renders the reversal plan and wires the Undo button.
//   connMeta: the target connection's metadata ({env_role, label, ...}) — either the
//     explicit `conn` passed to openUndoDialog, or the active connection's metadata
//     fetched via api.connectionMeta(); null in local mode.
//   cid: optional explicit connection id to scope api.undoExecute to (undefined ->
//     api.undoExecute falls back to the active connection, unchanged behavior).
//   In hosted mode with env_role === "prod": a typed-confirm input blocks execute until
//   the user types the connection label (or "CONFIRM" when the label is empty).
function _renderPreview(dlg, plan, selector, onDone, connMeta, cid) {
  const steps = plan.steps || [];
  const nRev  = steps.filter((s) => s.reversibility === "reversible").length;
  const nRisk = steps.filter((s) => s.reversibility === "risky").length;
  const nIrr  = steps.filter((s) => s.reversibility === "irreversible").length;

  // Prod typed-confirm: required when the active connection has env_role === "prod".
  // The expected token is the connection label; falls back to the literal "CONFIRM".
  const isProd = !!(connMeta && connMeta.env_role === "prod");
  const expectedToken = isProd ? (connMeta.label || "CONFIRM") : null;

  renderDialog(dlg, `
    <div class="rw-head">
      <span class="rw-head-title"><i class="ic ic-undo"></i> Undo ${steps.length} change${steps.length !== 1 ? "s" : ""}</span>
      <button class="rw-x btn-icon" id="rwX" data-close aria-label="Close"><i class="ic ic-x"></i></button>
    </div>
    <div class="rw-sub">Reverting newest-first. Stops on first failure (resumable).</div>
    <div class="rw-steps">${_stepRows(steps)}</div>
    <div class="rw-opts">
      <label class="rw-ck${nRisk === 0 ? " rw-dis" : ""}">
        <input type="checkbox" id="rwRisk"${nRisk === 0 ? " disabled" : ""}> Include ${nRisk} risky step${nRisk !== 1 ? "s" : ""}
      </label>
      <label class="rw-ck">
        <input type="checkbox" id="rwForce"> Force past drift (entries edited since capture)
      </label>
    </div>
    ${isProd ? `
    <div class="rw-prod-gate">
      <div class="rw-sub rw-prod-warn"><i class="ic ic-sm ic-alert"></i> Production environment — type <b>${esc(expectedToken)}</b> to confirm.</div>
      <input class="rw-typed-input" id="rwTypedConfirm" type="text" placeholder="${esc(expectedToken)}" autocomplete="off" spellcheck="false" />
    </div>` : ""}
    <div class="rw-foot">
      <span class="rw-sum">${nRev} reversible · ${nRisk} risky · ${nIrr} record-only</span>
      <span class="rw-sp">
        <button class="btn" id="rwCancel" data-close>Cancel</button>
        <button class="btn btn-primary" id="rwGo"${isProd ? " disabled" : ""}>
          <i class="ic ic-sm ic-undo"></i> <span id="rwGoN">Undo ${nRev}</span>
        </button>
      </span>
    </div>`);

  const risk       = dlg.querySelector("#rwRisk");
  const force      = dlg.querySelector("#rwForce");
  const goN        = dlg.querySelector("#rwGoN");
  const goBtn      = dlg.querySelector("#rwGo");
  const typedInput = dlg.querySelector("#rwTypedConfirm");

  function recount() {
    goN.textContent = `Undo ${nRev + (risk && risk.checked ? nRisk : 0)}`;
  }
  if (risk) risk.addEventListener("change", recount);

  // Prod typed-confirm gate: enable Undo only when the typed value matches the expected
  // token exactly.  typedInput is only present when isProd === true.
  if (isProd && typedInput) {
    typedInput.addEventListener("input", () => {
      goBtn.disabled = typedInput.value.trim() !== expectedToken;
    });
  }

  // #rwX + #rwCancel close via [data-close] (auto-wired by renderDialog).
  goBtn.addEventListener("click", async () => {
    if (isProd && typedInput && typedInput.value.trim() !== expectedToken) return;
    goBtn.disabled = true;
    const execSel = {
      ...selector,
      include_risky: !!(risk && risk.checked),
      force:         !!(force && force.checked),
    };
    // Pass typed_confirm for prod connections (undefined in local or non-prod mode).
    const executeOpts = (isProd && typedInput)
      ? { typed_confirm: typedInput.value.trim() }
      : {};
    try {
      // api.undoExecute routes to the hosted /undo endpoint in hosted mode, or falls
      // back to the local rewindExecute tool.  cid (from the optional conn param)
      // scopes the call to an explicit target connection; undefined falls back to
      // the active connection (unchanged behavior).
      const res = await api.undoExecute(execSel, executeOpts, cid);
      _renderResult(dlg, res);
    } catch (e) {
      goBtn.disabled = false;
      _renderExecuteError(dlg, e);
    } finally {
      // onDone fires regardless so timeline refreshes (entry may have changed state)
      if (typeof onDone === "function") onDone();
    }
  });
}

function _renderResult(dlg, res) {
  const partial = res.status === "partial";
  const notAtt  = res.not_attempted ? ` · not attempted ${res.not_attempted}` : "";
  renderDialog(dlg, `
    <div class="rw-head">
      <span class="rw-head-title"><i class="ic ic-check"></i> Done</span>
    </div>
    <div class="rw-sub">
      Reversed ${res.reversed ?? 0} · skipped ${res.skipped ?? 0} · failed ${res.failed ?? 0}${notAtt}.
    </div>
    ${partial ? `<div class="rw-partial"><span class="rev-badge rev-risk"><span class="rev-dot"></span>Partial — re-run to resume</span></div>` : ""}
    <div class="rw-foot rw-foot-end">
      <span class="rw-sp">
        <button class="btn btn-primary" id="rwClose" data-close>Close</button>
      </span>
    </div>`);
}

function _renderExecuteError(dlg, err) {
  // Show an error strip inside the dialog without crashing; Undo button stays clickable
  const existing = dlg.querySelector(".rw-err");
  if (existing) existing.remove();
  const strip = document.createElement("div");
  strip.className = "rw-err";
  strip.textContent = "Undo failed: " + (err.message || String(err));
  // Insert before foot
  const foot = dlg.querySelector(".rw-foot");
  if (foot) foot.insertAdjacentElement("beforebegin", strip);
}

// ---------------------------------------------------------------------------
// Public export
// ---------------------------------------------------------------------------

export async function openUndoDialog(selector, onDone, conn = null) {
  const dlg = _ensure();
  renderDialog(dlg, `<div class="rw-body rw-loading">Loading preview…</div>`);

  // conn (optional): an explicit target connection { cid, label, env_role } —
  // e.g. a promotion target that isn't the user's active connection.  When
  // provided, skip the active-connection lookup and use it directly for both
  // the prod typed-confirm gate and the api calls below.  When absent, fetch
  // the active connection's metadata exactly as before (returns null in
  // local/desktop mode, where api.connectionMeta() returns null because
  // getActiveConnection() is null).
  const connMeta = conn || await api.connectionMeta().catch(() => null);
  const cid = conn?.cid;

  let plan;
  try {
    // api.undoPreview routes to POST /api/connections/{cid}/undo/preview in hosted
    // mode or falls back to the local rewind_preview tool.  cid scopes it to the
    // explicit conn when given; undefined falls back to the active connection.
    plan = await api.undoPreview(selector, cid);
  } catch (e) {
    renderDialog(dlg, `
      <div class="rw-head">
        <span class="rw-head-title"><i class="ic ic-alert"></i> Preview failed</span>
        <button class="rw-x btn-icon" id="rwX" data-close aria-label="Close"><i class="ic ic-x"></i></button>
      </div>
      <div class="rw-sub rw-err-text">${esc(e.message || String(e))}</div>
      <div class="rw-foot rw-foot-end">
        <span class="rw-sp"><button class="btn" id="rwClose" data-close>Close</button></span>
      </div>`);
    return;
  }

  _renderPreview(dlg, plan, selector, onDone, connMeta, cid);
}
